import { Fragment, useMemo } from 'react';
import { Tooltip } from 'antd';
import { useDispatch, useSelector } from 'react-redux';

import { TradeshowStatus } from 'models/tradeshowStatus';
import { colours } from 'utils/theme';
import { Icon } from 'components/common';
import { format, isAfter } from 'utils/date';
import { setSort } from 'actions/calendar';
import { getTableSort } from 'selectors/calendar';
import { useAuth } from 'hooks';

import { headerKeys } from './constants';
import sortFunc from './sort';
import {
  StyledTable,
  Status,
  InlineFlexGroup,
  VirtualLocationPip,
} from './styled';

import ViewContactsButton from './ActionButtons/ViewContactsButton';
import ExportTradeshowButton from './ActionButtons/ExportTradeshowButton';
import RegisterInterestButton from './ActionButtons/RegisterInterestButton';
import { toDateOnly, formatEstimatedDate } from './helpers';

const Table = ({ data, loading }) => {
  const dispatch = useDispatch();
  const sort = useSelector(getTableSort);
  const { user } = useAuth();
  const handleSortChange = sortKey => {
    dispatch(setSort(sortKey));
  };

  const sortedData = useMemo(
    () =>
      data ? [...data].sort((a, b) => sortFunc(a, b, sort.by, sort.dir)) : [],
    [data, sort]
  );

  const getTradeshowStatus = tradeshow => {
    if (tradeshow.statusId === TradeshowStatus.Cancelled) {
      return <Status status='cancelled'>Cancelled</Status>;
    }

    if (tradeshow.statusId === TradeshowStatus.Postponed) {
      return <Status status='postponed'>Postponed</Status>;
    }

    var now = new Date();
    if (tradeshow.startDate && tradeshow.endDate) {
      // Normalize startDate to start of day, endDate to end of day
      const startDate = new Date(tradeshow.startDate);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(tradeshow.endDate);
      endDate.setHours(23, 59, 59, 999);

      if (isAfter(now, endDate)) {
        return <Status status='completed'>Completed</Status>;
      }
      if (isAfter(startDate, now)) {
        return <Status status='upcoming'>Upcoming</Status>;
      }
      return <Status status='ongoing'>Ongoing</Status>;
    }

    // This shouldn't happen since the dates are not nullable in the database,
    // but just in case the dates are not set, we return "Dates TBC" so that it isn't confused with the actual TBC flag.
    return <Status status='tbc'>Dates TBC</Status>;
  };

  const getLocation = tradeshow => {
    // Physical / In-Person Location
    let location = [tradeshow.city, tradeshow.state, tradeshow.country]
      .map(s => s && s.trim())
      .filter(Boolean)
      .join(', ');

    let physical = null;
    if (!tradeshow.isTbc && location) {
      physical = (
        <InlineFlexGroup>
          <Icon icon='map' size='1em' color={colours.grey} />
          {location}
        </InlineFlexGroup>
      );
    }

    // Virtual Location
    let virtual = null;
    if (!tradeshow.isTbc && tradeshow.isVirtual) {
      virtual = (
        <VirtualLocationPip>
          <Icon icon='video' size='1x' color={colours.grey} />
          Virtual
        </VirtualLocationPip>
      );
    }

    if (!physical && !virtual) {
      // If neither physical nor virtual location is available, return 'Unknown' or 'TBC'.
      // "TBC" is for tradeshows that are explicitly marked as TBC.
      // "Unknown" should not happen, as validation should prevent it when importing tradeshows.
      // But an "Unknown" state will be the case initially for any tradeshows that were not backfilled with location data, and not set to TBC.
      return <span>{tradeshow.isTbc ? 'TBC' : 'Unknown'}</span>;
    } else {
      // Hybrid locations will have both physical and virtual locations
      return (
        <InlineFlexGroup>
          {physical}
          {virtual}
        </InlineFlexGroup>
      );
    }
  };

  const getPlatformStatus = tradeshow => {
    if (tradeshow.availableToDownload) {
      return <Status status='inPlatform'>In Platform</Status>;
    }

    return <Status status='pending'>Pending</Status>;
  };

  const getShowContacts = tradeshow => {
    if (tradeshow.availableToDownload) {
      return (
        <ViewContactsButton name={tradeshow.name} date={tradeshow.startDate} />
      );
    }

    if (tradeshow.statusId === TradeshowStatus.Cancelled) {
      return <span>Not Available</span>;
    }

    const today = toDateOnly(new Date());
    const startDate = toDateOnly(tradeshow.startDate);

    if (today >= startDate) {
      return <span>Coming Soon</span>;
    }

    if (tradeshow.estimatedAvailabilityDate != null) {
      return (
        <Tooltip title='Dates are indicative only and may change depending on when event organisers release the programme.'>
          <span>
            {formatEstimatedDate(tradeshow.estimatedAvailabilityDate)}
          </span>
        </Tooltip>
      );
    }

    return <span>Coming Soon</span>;
  };

  const getRegisteredInterest = (tradeshow, user) => {
    return tradeshow.availableToDownload && !user.disableExport ? (
      <ExportTradeshowButton name={tradeshow.name} date={tradeshow.startDate} />
    ) : (
      <RegisterInterestButton
        id={tradeshow.id}
        isRegistered={tradeshow.isUserRegistered}
        rowData={tradeshow}
      />
    );
  };

  return (
    <StyledTable
      config={{
        headers: [
          {
            label: 'Tradeshow Status',
            key: headerKeys.tradeshowStatus,
            minWidth: 80,
          },
          {
            label: 'Tradeshow Name',
            key: headerKeys.name,
            minWidth: 180,
            maxContentWidth: 350,
          },
          { label: 'Date', key: headerKeys.startDate, minWidth: 120 },
          { label: 'Location', key: headerKeys.location, minWidth: 50 },
          {
            label: 'Platform Status',
            key: headerKeys.platformStatus,
            minWidth: 50,
          },
          {
            label: 'Contact Access',
            key: headerKeys.contactAccess,
            minWidth: 50,
          },
          {
            label: 'Registered Interest',
            key: headerKeys.registeredInterest,
            testId: 'actions',
            minWidth: 50,
          },
        ],
        createRow: r => (
          <Fragment key={r.id}>
            {getTradeshowStatus(r)}
            <div>{r.name}</div>
            <Tooltip title={`End date: ${format(r.endDate)}`} placement='right'>
              <div>{format(r.startDate)}</div>
            </Tooltip>
            {getLocation(r)}
            {getPlatformStatus(r)}
            {getShowContacts(r)}
            {getRegisteredInterest(r, user)}
          </Fragment>
        ),
      }}
      data={sortedData}
      loading={loading}
      paginate
      sortable
      onSortChange={handleSortChange}
      sort={sort.by}
      sortDir={sort.dir}
      onSort={sortFunc}
    />
  );
};

export default Table;
